import React, { useEffect, useMemo, useState } from 'react';

import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Home } from '@mui/icons-material';
import { Storage } from 'aws-amplify';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  createQualification,
  createQualificationDocument,
  evaluateDocuments,
  getAMC,
  getClient,
  getQualification,
  removeQualification,
  removeQualificationDocument,
  updateQualification,
} from './operations';
import {
  Breadcrumbs,
  Button,
  FileUploadButton,
  Footer,
  FormContainer,
  FormTextField,
  Modal,
  ModalConfirmation,
  SubHeader,
} from '../../../../components';
import { formatIsoToDate } from '../../../../utils';
import { createUUID } from '../../../../utils/createUUID';
import { currentUserIdentity } from '../../../../utils/currentUser';
import { getItems } from '../QualificationList/operations';
import './styles.scoped.css';

const LEVELS = ['Single Client', 'AMC', 'CLD Default'];

// Qualification documents are checklist PDFs that can run large. FileUploadButton otherwise defaults
// to REACT_APP_PDF_MAX_SIZE (the 800 KB expense-receipt cap), which wrongly rejects them. Cap at
// ~20 MB — well above real checklists and under the Agent Exchange gateway's 22.5 MB per-file limit.
const QUAL_DOC_MAX_KB = 20000;

// Rules-readiness badge copy (AiClientQualification.rulesStatus). GENERATING is set while the
// async distill worker runs; READY means loadRules can audit against the cached rules.md.
const RULES_STATUS_LABEL = {
  GENERATING: 'Generating audit rules...',
  READY: 'Audit rules ready',
  FAILED: 'Rule generation failed',
  STALE: 'Documents changed — re-evaluate',
};

// CP-836 — Add/Edit Client Qualification (mockup page 26). Clones the Checklist Details interaction
// set but built on the house Form components + validate-on-press (golden rule 4).
const QualificationDetails = () => {
  const navigation = useNavigate();
  const location = useLocation();
  const { listData } = useSelector(state => state.qualificationList);

  const [id, setId] = useState('');
  const [editing, setEditing] = useState(false);
  const [qualLevel, setQualLevel] = useState(null);

  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientError, setClientError] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);

  const [amcCode, setAmcCode] = useState('');
  const [amcName, setAmcName] = useState('');
  const [amcError, setAmcError] = useState(false);
  const [amcLoading, setAmcLoading] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [errors, setErrors] = useState({});
  const [posting, setPosting] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  const [showCancel, setShowCancel] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [showCldDisabled, setShowCldDisabled] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [evalResult, setEvalResult] = useState(null); // 'success' | 'failure'
  const [evalFile, setEvalFile] = useState('');
  const [rulesStatus, setRulesStatus] = useState(null); // null | GENERATING | READY | FAILED | STALE

  // A CLD Default already exists (other than this record) → block selecting the level.
  const cldExists = useMemo(() => listData.find(q => q.qualLevel === 'CLD Default' && q.id !== id), [listData, id]);

  useEffect(() => {
    const segments = location.pathname.split('/');
    const routeId = segments.pop();
    setId(routeId);
    setEditing(segments.includes('edit-details'));
    getItems(); // populate listData for the CLD-Default and duplicate guards
  }, [location]);

  useEffect(() => {
    if (!editing || !id) return undefined;
    let cancelled = false;
    (async () => {
      const data = await getQualification(id);
      if (cancelled || !data) return;
      setQualLevel(data.qualLevel);
      if (data.qualLevel === 'Single Client') {
        setClientId(data.clientId ? String(data.clientId) : '');
        setClientName(data.clientName || '');
      }
      if (data.qualLevel === 'AMC') {
        setAmcCode(data.accountMasterCode || '');
        setAmcName(data.amcName || '');
      }
      setDocuments(data.documents?.items || []);
      setRulesStatus(data.rulesStatus || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, id]);

  const clearError = key => setErrors(prev => (prev[key] ? { ...prev, [key]: undefined } : prev));

  const fetchClient = async value => {
    if (!value) return;
    setClientError(false);
    setClientLoading(true);
    const response = await getClient(value);
    if (response?.response_code === 200 && response?.response_body) {
      setClientName(response.response_body.clientName || '');
    } else {
      setClientError(true);
      setClientName('');
    }
    setClientLoading(false);
  };

  const fetchAMC = async value => {
    if (!value) return;
    setAmcError(false);
    setAmcLoading(true);
    const response = await getAMC(value);
    if (response?.response_code === 200 && response?.response_body?.length > 0) {
      setAmcCode(response.response_body[0]?.Code || value);
      setAmcName(response.response_body[0]?.Description || '');
    } else {
      setAmcError(true);
      setAmcName('');
    }
    setAmcLoading(false);
  };

  const selectLevel = level => {
    if (level === 'CLD Default' && cldExists) {
      setShowCldDisabled(true);
      return;
    }
    clearError('level');
    setQualLevel(level);
  };

  const handleUpload = async ({ file }) => {
    try {
      setUploading(true);
      const key = `dqf-qualifications/${id}/${createUUID()}-${file.name}`;
      await Storage.put(key, file, { level: 'public', contentType: file.type });
      const { email, fullName } = await currentUserIdentity();
      const created = await createQualificationDocument({
        documentName: key,
        originalFileName: file.name,
        uploadedByEmail: email,
        uploadedByFullName: fullName,
        aiClientQualificationDocumentsId: id,
      });
      if (created?.id) {
        setDocuments(prev => [
          ...prev,
          { id: created.id, documentName: key, originalFileName: file.name, uploadedByFullName: fullName },
        ]);
        clearError('documents');
        // Docs changed → cached rules are stale; require re-running Evaluate Documents.
        if (editing) {
          await updateQualification({ id, rulesStatus: 'STALE' });
          setRulesStatus('STALE');
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setUploading(false);
    }
  };

  const openDocument = async doc => {
    try {
      const url = await Storage.get(doc.documentName, {
        expires: Number(process.env.REACT_APP_AMPLIFY_STORAGE_GET_EXPIRES),
        level: 'public',
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.log(e);
    }
  };

  const confirmDeleteDoc = async () => {
    if (!docToDelete) return;
    await removeQualificationDocument(docToDelete.id);
    try {
      await Storage.remove(docToDelete.documentName, { level: 'public' });
    } catch (e) {
      console.log(e);
    }
    setDocuments(prev => prev.filter(d => d.id !== docToDelete.id));
    // Docs changed → cached rules are stale; require re-running Evaluate Documents.
    if (editing) {
      await updateQualification({ id, rulesStatus: 'STALE' });
      setRulesStatus('STALE');
    }
    setDocToDelete(null);
  };

  // After a successful kick-off the distill runs in the background; poll the record so the
  // badge flips GENERATING -> READY/FAILED without a manual reload (~2 min ceiling).
  const pollRulesStatus = async () => {
    for (let i = 0; i < 24; i += 1) {
      await new Promise(resolve => {
        setTimeout(resolve, 5000);
      });
      const data = await getQualification(id);
      const status = data?.rulesStatus;
      if (status && status !== 'GENERATING') {
        setRulesStatus(status);
        return;
      }
    }
  };

  const handleEvaluate = async () => {
    if (!documents.length) {
      setErrors(prev => ({ ...prev, documents: 'Please upload at least one qualification document.' }));
      return;
    }
    setEvaluating(true);
    try {
      const result = await evaluateDocuments(id);
      if (result?.response_body?.recognized) {
        setEvalResult('success');
        // Docs ingested; the distill runs in the background — track it to the badge.
        setRulesStatus('GENERATING');
        pollRulesStatus();
      } else {
        setEvalFile(result?.response_body?.failedFile || documents[0]?.originalFileName || 'the uploaded document');
        setEvalResult('failure');
      }
    } catch (e) {
      console.log(e);
      setEvalFile(documents[0]?.originalFileName || 'the uploaded document');
      setEvalResult('failure');
    } finally {
      setEvaluating(false);
    }
  };

  const validate = () => {
    const next = {};
    if (!qualLevel) {
      next.level = 'Please select a level.';
    } else if (qualLevel === 'Single Client' && (!clientId || clientError)) {
      next.clientId = 'Please enter a valid Client ID.';
    } else if (qualLevel === 'AMC' && (!amcCode || amcError)) {
      next.amcCode = 'Please enter a valid AMC Code.';
    }
    return next;
  };

  // Business rule (page 26): one qualification record per client / AMC / CLD-default id.
  const isDuplicate = () => {
    if (editing) return false;
    return listData.some(q => {
      if (q.id === id) return false;
      if (qualLevel === 'CLD Default') return q.qualLevel === 'CLD Default';
      if (qualLevel === 'AMC') return q.qualLevel === 'AMC' && q.accountMasterCode === amcCode;
      return q.qualLevel === 'Single Client' && String(q.clientId) === String(clientId);
    });
  };

  const buildInput = () => {
    const input = {
      id,
      qualLevel,
      category: 'Qualifications',
      clientId: qualLevel === 'Single Client' ? parseInt(clientId, 10) : 0,
      clientName: qualLevel === 'Single Client' ? clientName : '',
      accountMasterCode: qualLevel === 'AMC' ? amcCode : '',
      amcName: qualLevel === 'AMC' ? amcName : '',
    };
    // On create, any uploaded docs still need evaluating → block the audit until then.
    // On edit, leave rulesStatus alone (doc add/remove already invalidates it live).
    if (!editing && documents.length) input.rulesStatus = 'STALE';
    return input;
  };

  const handleSave = async () => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    if (isDuplicate()) {
      setShowDuplicate(true);
      return;
    }
    setPosting(true);
    const input = buildInput();
    const result = editing ? await updateQualification(input) : await createQualification(input);
    setPosting(false);
    if (!result?.errors) {
      navigation(-1);
    }
  };

  const handleDelete = async () => {
    await Promise.all(documents.map(doc => removeQualificationDocument(doc.id)));
    await removeQualification(id);
    navigation(-1);
  };

  return (
    <>
      <SubHeader
        text="Centerline Driver Qualification"
        buttons={[
          { icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigation('/driver-qualification') },
        ]}
      />
      <Breadcrumbs
        leftTitle="DQF Home"
        middleTitle="Qualification List"
        midItemAction={() => navigation('/qualification-list')}
        rightTitle={editing ? `Qualification ${id}` : 'Add Client Qualification'}
        route="/driver-qualification"
      />
      <div className="wrap">
        <FormContainer className="box">
          <div className="box-header">
            <span className="box-title">{editing ? 'Edit Client Qualification' : 'Add Client Qualification'}</span>
            <span className="box-id">{id}</span>
          </div>

          <p className="thick-label">Level</p>
          <div className="level-options">
            {LEVELS.map(level => {
              const disabled = level === 'CLD Default' && cldExists;
              return (
                <label key={level} className={`level-option${disabled ? ' disabled' : ''}`}>
                  <input
                    type="radio"
                    name="qualLevel"
                    checked={qualLevel === level}
                    onChange={() => selectLevel(level)}
                  />
                  <span>{level}</span>
                </label>
              );
            })}
          </div>
          {errors.level && <div className="field-error">{errors.level}</div>}

          {qualLevel === 'Single Client' && (
            <div className="lookup-row">
              <FormTextField
                label="Client ID"
                value={clientId}
                name="clientId"
                placeholder="Enter ID..."
                onChange={e => {
                  setClientId(e.target.value);
                  setClientName('');
                  clearError('clientId');
                }}
                onBlur={() => fetchClient(clientId)}
                error={!!errors.clientId || clientError}
                errorMessage={clientError ? 'Please enter a valid Client ID.' : errors.clientId}
              />
              <div className="lookup-value">
                <span className="lookup-label">Client Name</span>
                {clientLoading ? <Spinner animation="border" size="sm" /> : <span>{clientName}</span>}
              </div>
            </div>
          )}

          {qualLevel === 'AMC' && (
            <div className="lookup-row">
              <FormTextField
                label="AMC Code"
                value={amcCode}
                name="amcCode"
                placeholder="Enter AMC Code..."
                onChange={e => {
                  setAmcCode(e.target.value);
                  setAmcName('');
                  clearError('amcCode');
                }}
                onBlur={() => fetchAMC(amcCode)}
                error={!!errors.amcCode || amcError}
                errorMessage={amcError ? 'Please enter a valid AMC Code.' : errors.amcCode}
              />
              <div className="lookup-value">
                <span className="lookup-label">AMC Name</span>
                {amcLoading ? <Spinner animation="border" size="sm" /> : <span>{amcName}</span>}
              </div>
            </div>
          )}

          {qualLevel === 'CLD Default' && <p className="cld-note">Centerline Default — National Qualifications</p>}

          <p className="thick-label">Qualification Documents</p>
          <FileUploadButton
            func={handleUpload}
            uploadStart={() => setUploading(true)}
            uploadEnd={() => setUploading(false)}
            uploadError={() => setUploading(false)}
            fileTypes={['PDF']}
            maxSizeKb={QUAL_DOC_MAX_KB}
          />
          {uploading && (
            <div className="uploading">
              <Spinner animation="border" size="sm" /> <span>Uploading...</span>
            </div>
          )}

          {documents.length > 0 && (
            <table className="docs-table">
              <thead>
                <tr>
                  <th>Document Name</th>
                  <th>Uploaded By</th>
                  <th>Uploaded Date</th>
                  <th aria-label="Delete" />
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc.id}>
                    <td>
                      <button type="button" className="doc-link" onClick={() => openDocument(doc)}>
                        {doc.originalFileName}
                      </button>
                    </td>
                    <td>{doc.uploadedByFullName}</td>
                    <td>{doc.createdAt ? formatIsoToDate(doc.createdAt) : ''}</td>
                    <td className="doc-actions">
                      <button
                        type="button"
                        className="trash-btn"
                        aria-label={`Delete ${doc.originalFileName}`}
                        onClick={() => setDocToDelete(doc)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {errors.documents && <div className="field-error">{errors.documents}</div>}

          {rulesStatus && (
            <div className={`rules-status rules-status--${rulesStatus.toLowerCase()}`}>
              {RULES_STATUS_LABEL[rulesStatus] || rulesStatus}
            </div>
          )}

          <Button variant="primary" fullWidth className="evaluate-btn" disabled={evaluating} onPress={handleEvaluate}>
            {evaluating ? 'Evaluating...' : 'Evaluate Documents'}
          </Button>
        </FormContainer>

        <div className="actions">
          {editing && (
            <Button variant="outline" onPress={() => setShowDelete(true)}>
              Delete
            </Button>
          )}
          <Button variant="primary" disabled={posting} onPress={handleSave}>
            {posting ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="outline" onPress={() => setShowCancel(true)}>
            Cancel
          </Button>
        </div>
      </div>

      {showCancel && (
        <Modal
          isOpen
          title="Cancel pending changes?"
          primaryButtonTitle="Yes"
          secondaryButtonTitle="No"
          optionalClose
          bodyContent={false}
          secondaryOnClick={() => setShowCancel(false)}
          onClick={() => navigation(-1)}
        />
      )}
      {showDelete && (
        <Modal
          isOpen
          title="Delete this qualification?"
          primaryButtonTitle="Yes"
          secondaryButtonTitle="No"
          optionalClose
          bodyContent={false}
          secondaryOnClick={() => setShowDelete(false)}
          onClick={handleDelete}
        />
      )}
      {docToDelete && (
        <Modal
          isOpen
          title="Delete this document?"
          primaryButtonTitle="Delete"
          secondaryButtonTitle="Cancel"
          optionalClose
          bodyContent={false}
          secondaryOnClick={() => setDocToDelete(null)}
          onClick={confirmDeleteDoc}
        />
      )}
      {showCldDisabled && (
        <ModalConfirmation
          isOpen
          title="CLD Default Disabled"
          body="A CLD Default qualification already exists. To create a new one, edit or delete the existing record."
          primaryButton
          primaryButtonTitle="Ok"
          onClick={() => setShowCldDisabled(false)}
        />
      )}
      {showDuplicate && (
        <ModalConfirmation
          isOpen
          title="Duplicate Qualification"
          body="A qualification file for this ID already exists."
          primaryButton
          primaryButtonTitle="Ok"
          onClick={() => setShowDuplicate(false)}
        />
      )}
      {evalResult === 'success' && (
        <ModalConfirmation
          isOpen
          title="AI Evaluation of Qualifications"
          body="The AI assistant was able to recognize and identify all documents for comparing these qualifications to a Driver Qualification File."
          primaryButton
          primaryButtonTitle="Ok"
          onClick={() => setEvalResult(null)}
        />
      )}
      {evalResult === 'failure' && (
        <ModalConfirmation
          isOpen
          title="AI Evaluation of Qualifications"
          body={`The AI assistant was not able to identify the file named ${evalFile}. Please reach out to the Edge-Support-Desk@trueblue.com for assistance. Please include the Qualification File Number and a description of which file failed to be recognized.`}
          primaryButton
          primaryButtonTitle="Ok"
          onClick={() => setEvalResult(null)}
        />
      )}
      <Footer />
    </>
  );
};
export default QualificationDetails;
